import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
// Form components replaced with custom styled elements
import { GlobalContext } from '../../../api/GlobalContext';
import Modal from '../../Modals/Modal';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import OpenAIAssistant from './OpenAIAssistant'; // Adjust the path as needed
import VoiceAssistant from './SynapseBrain'; // Adjust the path as needed
import { VAStates } from './SynapseBrain';
import { isDemoMode, setDemoMode } from '../../../utils/demoStorage';

const TIMEOUT_DURATION = 30 * 60 * 1000; // 30 minutes

const SYNAPSE_SETTINGS_KEY = 'synapse_settings';
const VALID_SYNAPSE_MODES = ['clinic', 'conversation'];
const VALID_VERBOSITY_MODES = ['ultra', 'medium', 'normal'];

const getStoredSynapseMode = () => {
  try {
    const raw = localStorage.getItem(SYNAPSE_SETTINGS_KEY);
    if (!raw) return 'clinic';
    const parsed = JSON.parse(raw);
    const mode = parsed?.conversationMode;
    return VALID_SYNAPSE_MODES.includes(mode) ? mode : 'clinic';
  } catch {
    return 'clinic';
  }
};

const getStoredResponseStyle = () => {
  try {
    const raw = localStorage.getItem(SYNAPSE_SETTINGS_KEY);
    if (!raw) return 'medium';
    const parsed = JSON.parse(raw);
    const style = parsed?.responseStyle;
    return VALID_VERBOSITY_MODES.includes(style) ? style : 'medium';
  } catch {
    return 'medium';
  }
};

const saveSynapseSettings = (updates) => {
  try {
    const raw = localStorage.getItem(SYNAPSE_SETTINGS_KEY);
    const current = raw ? JSON.parse(raw) : {};
    const next = { ...current, ...updates };
    localStorage.setItem(SYNAPSE_SETTINGS_KEY, JSON.stringify(next));
  } catch (e) {
    console.warn('Synapse: could not save settings to localStorage', e);
  }
};

const Synapse = () => {
  const navigate = useNavigate();
  const { apiClient } = useContext(GlobalContext);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [synapseActive, setSynapseActive] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [vaState, setVaState] = useState(VAStates.INACTIVE);
  const [audioDevices, setAudioDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('default');
  const [synapseMode, setSynapseMode] = useState(getStoredSynapseMode); // 'clinic' or 'conversation'
  const [verbosityMode, setVerbosityMode] = useState(getStoredResponseStyle); // 'ultra' | 'medium' | 'normal'
  const [completedAction, setCompletedAction] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [restartKey, setRestartKey] = useState(0);
  const [demoModeToggle, setDemoModeToggle] = useState(0); // force re-render when toggling demo mode (localStorage)

  const assistantRef = useRef(null);
  const lastInputDevicesRef = useRef(null); // Track input devices to detect actual changes
  const deviceChangeTimeoutRef = useRef(null); // Debounce device changes
  const lastRaybanPresentRef = useRef(false); // Track Ray-Ban presence for start/stop
  const micPermissionGrantedRef = useRef(false); // Track if we've already requested mic permission

  // Identify Ray-Ban/RB Meta audio input devices by label
  const isRaybanDevice = (label) => {
    const text = (label || '').toLowerCase();
    return (
      text.includes('rb meta') ||
      text.includes('ray-ban') ||
      text.includes('rayban') ||
      text.includes('stories')
    );
  };

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  const handleButtonClick = () => {
    if (!isClicked) {
      setIsClicked(true);
      setShowInfoModal(true);
    }
  };

  const handleInfoClose = () => {
    setIsClicked(false);
    setIsHovered(false);
    setShowInfoModal(false);
  };

  const handleManualReset = async () => {
    if (
      assistantRef.current &&
      typeof assistantRef.current.resetConversationMemory === 'function'
    ) {
      handleInfoClose(); // Close modal first
      await assistantRef.current.resetConversationMemory('manual reset');
    }
  };

  const handleVerbosityChange = (mode) => {
    setVerbosityMode(mode);
    saveSynapseSettings({ responseStyle: mode });
    if (
      assistantRef.current &&
      typeof assistantRef.current.setVerbosity === 'function'
    ) {
      assistantRef.current.setVerbosity(mode);
    }
  };

  const handleModeChange = (mode) => {
    setSynapseMode(mode);
    saveSynapseSettings({ conversationMode: mode });
    if (
      assistantRef.current &&
      typeof assistantRef.current.setMode === 'function'
    ) {
      assistantRef.current.setMode(mode);
    }
  };

  /**
   * Helper function to restart Synapse
   * Gracefully stops the current session, recreates the VoiceAssistant (same as device-change effect),
   * then starts a fresh session so device id and instance are reset.
   * @param {string} reason - The reason for restarting (for logging/toast)
   */
  const restartSynapse = async (reason = 'manual restart') => {
    if (vaState === VAStates.ASSISTANT || vaState === VAStates.WAKE_WORD) {
      setVaState(VAStates.INACTIVE);
      await new Promise((resolve) => setTimeout(resolve, 100));
      console.log(`🔄 Restarting Synapse: ${reason}`);
      toast.info(`${reason} - restarting Synapse`);
      // Bump restartKey so the selectedDeviceId effect re-runs: cleanup old instance, create new one (resets device id)
      setRestartKey((k) => k + 1);
      setVaState(VAStates.WAKE_WORD);
      console.log('Synapse restarted');
    } else {
      console.log('Synapse is not active, no restart needed');
    }
  };

  // Enumerate audio input devices - only when modal is shown
  useEffect(() => {
    // Only request mic permission when modal is shown for the first time
    if (!showInfoModal || micPermissionGrantedRef.current) {
      return;
    }

    const getAudioDevices = async () => {
      let stream = null;
      try {
        // Request permission first (we'll stop this stream immediately after)
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micPermissionGrantedRef.current = true;

        const devices = await navigator.mediaDevices.enumerateDevices();
        const audioInputs = devices.filter(
          (device) => device.kind === 'audioinput',
        );
        setAudioDevices(audioInputs);

        // Set default device if available
        if (audioInputs.length > 0 && selectedDeviceId === 'default') {
          const defaultDevice =
            audioInputs.find((d) => d.deviceId === 'default') || audioInputs[0];
          setSelectedDeviceId(defaultDevice.deviceId);
        }

        // Initialize the last input devices reference
        const defaultDeviceId = stream
          .getAudioTracks()[0]
          ?.getSettings().deviceId;
        lastInputDevicesRef.current = JSON.stringify({
          devices: audioInputs.map((d) => `${d.deviceId}:${d.label}`).sort(),
          defaultDevice: defaultDeviceId,
        });
        console.log('🔄 Initial input state:', lastInputDevicesRef.current);

        // Initialize Ray-Ban presence state
        lastRaybanPresentRef.current = audioInputs.some((d) =>
          isRaybanDevice(d.label),
        );
        if (lastRaybanPresentRef.current) {
          console.log('👓 Ray-Ban device initially present');
        }
      } catch (error) {
        console.error('Error enumerating audio devices:', error);
        toast.error('Could not access audio devices');
      } finally {
        // Always release the permission stream after enumerating devices
        if (stream) {
          stream.getTracks().forEach((track) => track.stop());
        }
      }
    };

    getAudioDevices();
  }, [showInfoModal, selectedDeviceId]);

  // Listen for device changes (bluetooth/audio I/O changes) - only after permission granted
  useEffect(() => {
    // Only set up listener if we've already granted permission
    if (!micPermissionGrantedRef.current) {
      return;
    }

    const handleDeviceChange = () => {
      console.log('🔄 Device change event fired');

      // Debounce: clear any pending device change check
      if (deviceChangeTimeoutRef.current) {
        clearTimeout(deviceChangeTimeoutRef.current);
      }

      // Wait 300ms before checking, in case multiple events fire
      deviceChangeTimeoutRef.current = setTimeout(async () => {
        console.log('🔍 Checking device state after debounce...');

        // Get current devices and default device
        let stream = null;
        try {
          stream = await navigator.mediaDevices.getUserMedia({ audio: true });
          const defaultDeviceId = stream
            .getAudioTracks()[0]
            ?.getSettings().deviceId;

          const devices = await navigator.mediaDevices.enumerateDevices();
          const audioInputs = devices.filter(
            (device) => device.kind === 'audioinput',
          );

          // Create a representation that includes both device list AND default device
          const currentInputState = JSON.stringify({
            devices: audioInputs.map((d) => `${d.deviceId}:${d.label}`).sort(),
            defaultDevice: defaultDeviceId,
          });

          // Check if input state actually changed (devices OR default device)
          if (lastInputDevicesRef.current !== currentInputState) {
            const lastState = lastInputDevicesRef.current
              ? JSON.parse(lastInputDevicesRef.current)
              : null;
            const newState = JSON.parse(currentInputState);

            console.log('📢 Input device state changed:', {
              before: lastState,
              after: newState,
            });

            // Update the stored state
            lastInputDevicesRef.current = currentInputState;

            // Update the audio devices list
            setAudioDevices(audioInputs);

            // Detect Ray-Ban presence and auto start/stop/restart
            const raybanPresent = audioInputs.some((d) =>
              isRaybanDevice(d.label),
            );
            const wasRaybanPresent = lastRaybanPresentRef.current;
            if (raybanPresent !== wasRaybanPresent) {
              lastRaybanPresentRef.current = raybanPresent;
              if (raybanPresent) {
                console.log('👓 Ray-Ban connected');
                if (isDemoMode()) {
                  // Demo only: send them home and start/restart Synapse
                  navigate('/User/Home');
                }
                  
                if (synapseActive) {
                  // User has already started Comprehend Voice – restart to switch input cleanly.
                  toast.info('Ray-Ban connected - switching input (restart)');
                  await restartSynapse('Ray-Ban connected');
                } else {
                  // Start Synapse (DEMO ONLY!! So that it starts when they connect.)
                  toast.info('Ray-Ban connected - starting Comprehend Voice');
                  setSynapseActive(true);
                  // User has NOT started Comprehend Voice yet – do nothing.
                  console.log(
                    '🔄 Ray-Ban connected while Synapse inactive - no auto-start (no-op)',
                  );
                }
              } else {
                console.log('👓 Ray-Ban disconnected');
                if (synapseActive) {
                  toast.info(
                    'Ray-Ban disconnected - stopping Comprehend Voice',
                  );
                  setSynapseActive(false);
                }
              }
            } else {
              // No Ray-Ban presence change; ignore
              // console.log('No Ray-Ban presence change');
            }
          } else {
            console.log('🔇 No actual input state change detected, ignoring');
          }
        } catch (error) {
          console.error('Error checking device change:', error);
        } finally {
          if (stream) {
            stream.getTracks().forEach((track) => track.stop());
          }
        }
      }, 300);
    };

    // Add event listener for device changes
    navigator.mediaDevices.addEventListener('devicechange', handleDeviceChange);

    // Cleanup function
    return () => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        handleDeviceChange,
      );
      if (deviceChangeTimeoutRef.current) {
        clearTimeout(deviceChangeTimeoutRef.current);
      }
    };
  }, [synapseActive, showInfoModal]);

  const handleCommandSuccess = (actionName) => {
    setCompletedAction(actionName);
    setTimeout(() => setCompletedAction(null), 3000);
  };

  useEffect(() => {
    // Clean up old instance if it exists
    if (assistantRef.current) {
      assistantRef.current.stopSession();
    }

    // Create new instance with selected device and mode
    assistantRef.current = new VoiceAssistant(
      apiClient,
      setVaState,
      selectedDeviceId,
      handleCommandSuccess,
      synapseMode,
      setSynapseMode,
      verbosityMode,
      setVerbosityMode,
    );

    // Set 30 minute timeout
    const timeoutId = setTimeout(() => {
      if (assistantRef.current) {
        assistantRef.current.stopSession();
      }
    }, TIMEOUT_DURATION);

    // Cleanup function
    return () => {
      clearTimeout(timeoutId);
      if (assistantRef.current) {
        assistantRef.current.stopSession();
      }
    };
  }, [selectedDeviceId, restartKey]);

  useEffect(() => {
    if (synapseActive && assistantRef.current) {
      assistantRef.current.startSession();
      // toast.info('Starting Synapse'); // Lower in the tree we tell them once it's actually ready
    } else if (
      assistantRef.current &&
      assistantRef.current.state != VAStates.INACTIVE
    ) {
      assistantRef.current.stopSession();
      toast.info('Stopped Comprehend Voice');
    }
  }, [synapseActive, restartKey]);

  // Determine background color based on VA state
  const getBackgroundColor = () => {
    if (isHovered || isClicked) return 'var(--primary)';
    switch (vaState) {
      case VAStates.INACTIVE:
        return 'grey';
      case VAStates.WAKE_WORD:
        return '#4A90E2'; // Blue for passive listening
      case VAStates.ASSISTANT:
        return 'var(--primary)'; // Green for conversational mode
      default:
        return 'grey';
    }
  };

  // Determine size based on VA state and hover
  const getSize = () => {
    if (isHovered || isClicked) return '50px';
    if (vaState === VAStates.ASSISTANT) return '60px'; // Larger in conversational mode
    return '30px';
  };

  const buttonStyle = {
    position: 'fixed',
    bottom: '20px',
    left: '20px',
    zIndex: 1000,
    borderRadius: '50%',
    width: getSize(),
    height: getSize(),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: getBackgroundColor(),
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    boxShadow:
      vaState === VAStates.ASSISTANT
        ? '0 0 20px rgba(76, 175, 80, 0.6)'
        : 'none',
    animation:
      vaState !== VAStates.INACTIVE
        ? 'synapse-pulse 2s ease-in-out infinite'
        : 'none',
  };

  const getIconSize = () => {
    if (isHovered || isClicked) return '1em';
    if (vaState === VAStates.ASSISTANT) return '1.2em'; // Larger icon in conversational mode
    return '0.8em';
  };

  const iconStyle = {
    fontSize: getIconSize(),
    color: 'white',
    transition: 'font-size 0.3s ease',
    textShadow: 'none',
  };

  // Get status text based on state
  const getStatusText = () => {
    // Show completed action if available
    if (completedAction) {
      // Format action name: remove underscores and capitalize first letter
      const formattedAction = completedAction
        .replace(/_/g, ' ')
        .toLowerCase()
        .replace(/^\w/, (c) => c.toUpperCase());
      return `${formattedAction} completed`;
    }

    switch (vaState) {
      case VAStates.WAKE_WORD:
        return 'Say "Hey Comprehend" to activate';
      case VAStates.ASSISTANT:
        return 'Ready for your command';
      case VAStates.INACTIVE:
      default:
        return '';
    }
  };

  return (
    <>
      <div
        style={{
          position: 'fixed',
          bottom: '20px',
          left: '20px',
          zIndex: 1000,
        }}
      >
        <button
          onClick={handleButtonClick}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          style={buttonStyle}
          aria-label={getStatusText() || 'Start Comprehend Voice'}
        >
          <i className="fa fa-headphones" style={iconStyle}></i>
        </button>

        {/* Hover Tooltip - only show when inactive */}
        {isHovered && !showInfoModal && vaState === VAStates.INACTIVE && (
          <div
            style={{
              position: 'absolute',
              bottom: '50px',
              left: '0',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              fontWeight: '500',
              pointerEvents: 'none',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              zIndex: 1001,
            }}
          >
            Start Comprehend Voice
            <div
              style={{
                position: 'absolute',
                bottom: '-1px',
                left: '20px',
                transform: 'rotate(45deg)',
                width: '8px',
                height: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              }}
            />
          </div>
        )}

        {/* Status Label - show when active */}
        {getStatusText() && (
          <div
            style={{
              position: 'absolute',
              bottom: '50px',
              left: '0',
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              whiteSpace: 'nowrap',
              fontWeight: '500',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              zIndex: 1001,
            }}
          >
            {getStatusText()}
            <div
              style={{
                position: 'absolute',
                bottom: '-1px',
                left: '20px',
                transform: 'rotate(45deg)',
                width: '8px',
                height: '8px',
                backgroundColor: 'rgba(0, 0, 0, 0.9)',
              }}
            />
          </div>
        )}
      </div>
      {/* /* Recording Description Modal */}
      <Modal
        isOpen={showInfoModal}
        onClose={handleInfoClose}
        buttons={[
          {
            color: 'secondary',
            label: 'Cancel',
            onClick: handleInfoClose,
          },
          {
            color: 'warning',
            label: 'Reset',
            onClick: handleManualReset,
          },
          {
            color: synapseActive ? 'danger' : 'primary',
            label: synapseActive ? 'Stop' : 'Start',
            onClick: () => {
              handleInfoClose();
              if (synapseActive) {
                setSynapseActive(false);
              } else {
                setSynapseActive(true);
              }
            },
          },
        ]}
      >
        <h1>Comprehend Voice</h1>
        <p style={{ marginBottom: '16px', fontSize: '14px', color: '#525f7f' }}>
          Your AI assistant that listens in the background and helps with
          documentation.
        </p>
        <div
          style={{
            marginBottom: '10px',
            padding: '8px 10px',
            borderRadius: '8px',
            background: '#f8f9fe',
            border: '1px solid #e3e6f0',
            fontSize: '13px',
            color: '#525f7f',
            textAlign: 'left',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '2px',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ fontWeight: 600, color: '#1a237e' }}>
              💡 How to use
            </span>
            <span>
              Say <strong>"Hey Comprehend"</strong>, then say:
            </span>
          </div>
          <div
            style={{
              margin: 0,
              display: 'flex',
              flexWrap: 'wrap',
              gap: '4px 6px',
            }}
          >
            {[
              '"Start new visit"',
              '"Change patient"',
              '"Start recording"',
              '"Stop recording"',
              '"Finish my note"',
              '"Who\'s my next patient?"',
            ].map((cmd) => (
              <span
                key={cmd}
                style={{
                  padding: '2px 6px',
                  borderRadius: '999px',
                  backgroundColor: 'white',
                  border: '1px solid #e3e6f0',
                  color: '#324c7b',
                  fontSize: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                {cmd}
              </span>
            ))}
          </div>
        </div>

        {/* Advanced Settings Dropdown */}
        <div
          style={{
            marginTop: '8px',
          }}
        >
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            style={{
              width: '100%',
              padding: '12px 16px',
              borderRadius: '8px',
              border: '1px solid #e3e6f0',
              backgroundColor: showAdvanced ? '#f8f9fe' : 'white',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <i
                className="fa fa-cog"
                style={{ color: '#525f7f', fontSize: '14px' }}
              />
              <span
                style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#32325d',
                }}
              >
                Advanced Settings
              </span>
            </div>
            <i
              className={`fa fa-chevron-${showAdvanced ? 'up' : 'down'}`}
              style={{
                color: '#8898aa',
                fontSize: '12px',
                transition: 'transform 0.2s ease',
              }}
            />
          </button>

          {/* Collapsible content */}
          <div
            style={{
              maxHeight: showAdvanced ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                paddingTop: '16px',
              }}
            >
              {/* Verbosity Selection */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #f8f9fe 0%, #f1f3f9 100%)',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e3e6f0',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background:
                        verbosityMode === 'ultra'
                          ? 'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)'
                          : verbosityMode === 'medium'
                            ? 'linear-gradient(135deg, #11cdef 0%, #1171ef 100%)'
                            : 'linear-gradient(135deg, #2dce89 0%, #2dce89 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(94, 114, 228, 0.3)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>
                      {verbosityMode === 'ultra'
                        ? '⚡'
                        : verbosityMode === 'medium'
                          ? '➖'
                          : '📚'}
                    </span>
                  </div>
                  <label
                    style={{
                      margin: 0,
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#32325d',
                    }}
                  >
                    Response style
                  </label>
                </div>

                {/* Verbosity toggle buttons */}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '10px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleVerbosityChange('ultra')}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border:
                        verbosityMode === 'ultra'
                          ? '2px solid #5e72e4'
                          : '1px solid #d2d6dc',
                      backgroundColor:
                        verbosityMode === 'ultra'
                          ? 'rgba(94, 114, 228, 0.1)'
                          : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>⚡</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: verbosityMode === 'ultra' ? '600' : '500',
                        color:
                          verbosityMode === 'ultra' ? '#5e72e4' : '#525f7f',
                      }}
                    >
                      Brief
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerbosityChange('medium')}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border:
                        verbosityMode === 'medium'
                          ? '2px solid #11cdef'
                          : '1px solid #d2d6dc',
                      backgroundColor:
                        verbosityMode === 'medium'
                          ? 'rgba(17, 205, 239, 0.1)'
                          : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>➖</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: verbosityMode === 'medium' ? '600' : '500',
                        color:
                          verbosityMode === 'medium' ? '#11cdef' : '#525f7f',
                      }}
                    >
                      Normal
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleVerbosityChange('normal')}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border:
                        verbosityMode === 'normal'
                          ? '2px solid #2dce89'
                          : '1px solid #d2d6dc',
                      backgroundColor:
                        verbosityMode === 'normal'
                          ? 'rgba(45, 206, 137, 0.1)'
                          : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>📚</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: verbosityMode === 'normal' ? '600' : '500',
                        color:
                          verbosityMode === 'normal' ? '#2dce89' : '#525f7f',
                      }}
                    >
                      Wordy
                    </span>
                  </button>
                </div>

                <div
                  style={{
                    padding: '8px 12px',
                    background:
                      verbosityMode === 'ultra'
                        ? 'rgba(94, 114, 228, 0.08)'
                        : verbosityMode === 'medium'
                          ? 'rgba(17, 205, 239, 0.08)'
                          : 'rgba(45, 206, 137, 0.08)',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${
                      verbosityMode === 'ultra'
                        ? '#5e72e4'
                        : verbosityMode === 'medium'
                          ? '#11cdef'
                          : '#2dce89'
                    }`,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#525f7f',
                      lineHeight: '1.4',
                    }}
                  >
                    {verbosityMode === 'ultra'
                      ? '⚡ Super brief answers — one short line, no extra explanation.'
                      : verbosityMode === 'medium'
                        ? '➖ Normal responses in exactly one clear sentence unless you explicitly ask for more.'
                        : '📚 Wordy, detailed responses with reasoning and examples when helpful.'}
                  </span>
                </div>
              </div>

              {/* Mode Selection */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #f8f9fe 0%, #f1f3f9 100%)',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e3e6f0',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background:
                        synapseMode === 'clinic'
                          ? 'linear-gradient(135deg, #11cdef 0%, #1171ef 100%)'
                          : 'linear-gradient(135deg, #2dce89 0%, #2dce89 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow:
                        synapseMode === 'clinic'
                          ? '0 2px 8px rgba(17, 205, 239, 0.3)'
                          : '0 2px 8px rgba(45, 206, 137, 0.3)',
                      transition: 'all 0.3s ease',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>
                      {synapseMode === 'clinic' ? '🏥' : '💬'}
                    </span>
                  </div>
                  <label
                    style={{
                      margin: 0,
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#32325d',
                    }}
                  >
                    Mode
                  </label>
                </div>

                {/* Custom Toggle Buttons */}
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    marginBottom: '10px',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleModeChange('clinic')}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border:
                        synapseMode === 'clinic'
                          ? '2px solid #11cdef'
                          : '1px solid #d2d6dc',
                      backgroundColor:
                        synapseMode === 'clinic'
                          ? 'rgba(17, 205, 239, 0.1)'
                          : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>🏥</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight: synapseMode === 'clinic' ? '600' : '500',
                        color: synapseMode === 'clinic' ? '#11cdef' : '#525f7f',
                      }}
                    >
                      Clinic
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleModeChange('conversation')}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      borderRadius: '8px',
                      border:
                        synapseMode === 'conversation'
                          ? '2px solid #2dce89'
                          : '1px solid #d2d6dc',
                      backgroundColor:
                        synapseMode === 'conversation'
                          ? 'rgba(45, 206, 137, 0.1)'
                          : 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}
                  >
                    <span style={{ fontSize: '16px' }}>💬</span>
                    <span
                      style={{
                        fontSize: '13px',
                        fontWeight:
                          synapseMode === 'conversation' ? '600' : '500',
                        color:
                          synapseMode === 'conversation'
                            ? '#2dce89'
                            : '#525f7f',
                      }}
                    >
                      Conversation
                    </span>
                  </button>
                </div>

                <div
                  style={{
                    padding: '8px 12px',
                    background:
                      synapseMode === 'clinic'
                        ? 'rgba(17, 205, 239, 0.08)'
                        : 'rgba(45, 206, 137, 0.08)',
                    borderRadius: '6px',
                    borderLeft: `3px solid ${synapseMode === 'clinic' ? '#11cdef' : '#2dce89'}`,
                    transition: 'all 0.3s ease',
                  }}
                >
                  <span
                    style={{
                      fontSize: '12px',
                      color: '#525f7f',
                      lineHeight: '1.4',
                    }}
                  >
                    {synapseMode === 'clinic'
                      ? '📢 Say "Hey Comprehend" for each command'
                      : '🔄 Natural back-and-forth conversation'}
                  </span>
                </div>
              </div>

              {/* Demo mode (dev only) - localStorage only, no backend */}
              {apiClient?.isDev && (
                <div
                  style={{
                    background:
                      'linear-gradient(135deg, #f8f9fe 0%, #f1f3f9 100%)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #e3e6f0',
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '10px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '8px',
                          background:
                            'linear-gradient(135deg, #f5365c 0%, #f56036 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 2px 8px rgba(245, 54, 92, 0.3)',
                        }}
                      >
                        <span style={{ fontSize: '14px' }}>🔧</span>
                      </div>
                      <div>
                        <label
                          style={{
                            margin: 0,
                            fontWeight: '600',
                            fontSize: '14px',
                            color: '#32325d',
                          }}
                        >
                          Demo mode
                        </label>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#525f7f',
                            marginTop: '2px',
                          }}
                        >
                          Fake transcript, primer, Ray-Ban auto-navigate
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setDemoMode(!isDemoMode());
                        setDemoModeToggle((t) => t + 1);
                      }}
                      style={{
                        width: '48px',
                        height: '26px',
                        borderRadius: '13px',
                        border: 'none',
                        backgroundColor: isDemoMode()
                          ? '#5e72e4'
                          : '#e3e6f0',
                        cursor: 'pointer',
                        position: 'relative',
                        transition: 'background-color 0.2s ease',
                      }}
                      aria-label={isDemoMode() ? 'Disable demo mode' : 'Enable demo mode'}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          top: '2px',
                          left: isDemoMode() ? '24px' : '2px',
                          width: '22px',
                          height: '22px',
                          borderRadius: '50%',
                          backgroundColor: 'white',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                          transition: 'left 0.2s ease',
                        }}
                      />
                    </button>
                  </div>
                </div>
              )}

              {/* Microphone Selection */}
              <div
                style={{
                  background:
                    'linear-gradient(135deg, #f8f9fe 0%, #f1f3f9 100%)',
                  borderRadius: '12px',
                  padding: '16px',
                  border: '1px solid #e3e6f0',
                  transition: 'all 0.2s ease',
                  opacity: synapseActive ? 0.7 : 1,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background:
                        'linear-gradient(135deg, #5e72e4 0%, #825ee4 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 2px 8px rgba(94, 114, 228, 0.3)',
                    }}
                  >
                    <i
                      className="fa fa-microphone"
                      style={{ color: 'white', fontSize: '14px' }}
                    />
                  </div>
                  <label
                    style={{
                      margin: 0,
                      fontWeight: '600',
                      fontSize: '14px',
                      color: '#32325d',
                    }}
                  >
                    Microphone
                  </label>
                </div>
                <div style={{ position: 'relative' }}>
                  <select
                    id="microphoneSelect"
                    value={selectedDeviceId}
                    onChange={(e) => setSelectedDeviceId(e.target.value)}
                    disabled={synapseActive}
                    style={{
                      width: '100%',
                      padding: '10px 36px 10px 12px',
                      borderRadius: '8px',
                      border: '1px solid #d2d6dc',
                      backgroundColor: synapseActive ? '#f5f5f5' : 'white',
                      fontSize: '13px',
                      color: '#525f7f',
                      cursor: synapseActive ? 'not-allowed' : 'pointer',
                      appearance: 'none',
                      transition: 'all 0.2s ease',
                      outline: 'none',
                    }}
                    onFocus={(e) => {
                      if (!synapseActive)
                        e.target.style.borderColor = '#5e72e4';
                    }}
                    onBlur={(e) => {
                      e.target.style.borderColor = '#d2d6dc';
                    }}
                  >
                    {audioDevices.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label ||
                          `Microphone ${device.deviceId.substring(0, 8)}`}
                      </option>
                    ))}
                  </select>
                  <i
                    className="fa fa-chevron-down"
                    style={{
                      position: 'absolute',
                      right: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#8898aa',
                      fontSize: '12px',
                      pointerEvents: 'none',
                    }}
                  />
                </div>
                {synapseActive && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '8px',
                      padding: '6px 10px',
                      background: 'rgba(94, 114, 228, 0.1)',
                      borderRadius: '6px',
                    }}
                  >
                    <i
                      className="fa fa-info-circle"
                      style={{ color: '#5e72e4', fontSize: '11px' }}
                    />
                    <span style={{ fontSize: '11px', color: '#5e72e4' }}>
                      Stop Comprehend Voice to change microphone
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/** Docs & Updates Link */}
        <a
          href="https://comprehendpt.notion.site/Comprehend-Vision-2bc68e7a57a980729281d8e0e32b6a81?source=copy_link"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'block',
            textAlign: 'center',
            marginTop: '10px',
            fontSize: '12px',
            color: '#8898aa',
            textDecoration: 'none',
          }}
          onMouseEnter={(e) => (e.target.style.color = '#5e72e4')}
          onMouseLeave={(e) => (e.target.style.color = '#8898aa')}
        >
          View docs & updates →
        </a>

        {/* <OpenAIAssistant /> */}
      </Modal>
      <ToastContainer />
    </>
  );
};

export default Synapse;
